// 参加者の検索 UI (spec §4.7 検索全員共有機能)。
// 「全員に共有」チェックは spec L365 に従いデフォルト OFF。

import {
  useState,
  type FormEvent,
  type ReactElement,
} from 'react';
import { t, type Locale } from '@solo-webinar-copilot/i18n';
import { postSearch } from '../lib/api-client.ts';
import type { AppContext } from '../lib/zoom-sdk.ts';

export interface SearchBoxProps {
  ctx: AppContext;
  locale: Locale;
}

export function SearchBox({ ctx, locale }: SearchBoxProps): ReactElement {
  const [query, setQuery] = useState('');
  const [shareToAll, setShareToAll] = useState(false);
  const [sending, setSending] = useState(false);
  const [answer, setAnswer] = useState<string | null>(null);
  const [shared, setShared] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handle = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!query.trim()) return;
    setSending(true);
    setAnswer(null);
    setShared(false);
    const r = await postSearch(ctx, query.trim(), shareToAll);
    setSending(false);
    if (r.ok) {
      setAnswer(r.item.ai_response);
      if (r.item.shared_to_all === 1) setShared(true);
      setError(null);
      setQuery('');
    } else {
      setError(r.error);
    }
  };

  return (
    <section data-testid="search-box">
      <h2>{t('in_client.participant.search.title', locale)}</h2>
      {error ? (
        <p role="alert" data-testid="search-error">
          {t('in_client.error.network', locale)}: {error}
        </p>
      ) : null}
      <form onSubmit={handle}>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.currentTarget.value)}
          placeholder={t('in_client.participant.search.placeholder', locale)}
          data-testid="search-input"
          required
        />
        <label style={{ display: 'block', margin: '6px 0' }}>
          <input
            type="checkbox"
            checked={shareToAll}
            onChange={(e) => setShareToAll(e.currentTarget.checked)}
            data-testid="search-share-checkbox"
          />{' '}
          {t('in_client.participant.search.share_to_all', locale)}
        </label>
        <button type="submit" disabled={sending} data-testid="search-submit">
          {sending
            ? t('in_client.participant.search.sending', locale)
            : t('in_client.participant.search.send', locale)}
        </button>
      </form>
      {answer ? (
        <div data-testid="search-answer" style={{ marginTop: 8 }}>
          <h3>
            {t('in_client.participant.search.answer_label', locale)}{' '}
            <small style={{ opacity: 0.7 }}>
              [{t('in_client.ai.generated_label', locale)}]
            </small>
          </h3>
          <p style={{ whiteSpace: 'pre-wrap' }}>{answer}</p>
          <p style={{ fontSize: 12, opacity: 0.6 }}>
            {t('in_client.ai.disclaimer', locale)}
          </p>
          {shared ? (
            <p data-testid="search-shared">
              {t('in_client.participant.search.shared_notice', locale)}
            </p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
